import { useNavigate, useParams } from "react-router-dom";
import { HeaderWithBack } from "../../../components/structure/HeaderWithBack";
import { ContentBox } from "../../../components/structure/ContentBox";
import { ContentHeader } from "../../../components/structure/ContentHeader";
import { Button } from "../../../components/buttons/Button";
import React, { useEffect, useState } from "react";
import { useToastContext } from "../../../../contexts/ToastContext";
import { TinyMceEditor } from "./subcomponents/TinyMceEditor";
import { BasicDialog } from "../../../components/modal/BasicDialog";
import { TemplateProfile } from "../../../../core/entities/templateProfile";
import { TemplateType } from "../../../../core/entities/template";
import { ReplacementField } from "../../../../core/entities/replacementField";
import { useServiceContext } from "../../../../contexts/ServiceContext";
import { LoadingBoxFillComponent } from "../../../components/state/Loading";
import { ErrorPageScreen } from "../../public/ErrorPage/ErrorPage.Screen";

export function TemplateEditScreen() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const {
        templateService: {
            getTemplateProfile,
            updateDraft,
            publishDraft,
            getReplacementFields
        }
    } = useServiceContext();
    const { notifySuccess } = useToastContext();
    const [templateProfile, setTemplateProfile] = useState<TemplateProfile>();
    const [replacementFields, setReplacementFields] =
        useState<ReplacementField[]>();
    const [error, setError] = useState<any>();
    const [isConfirmBackOpen, setIsConfirmBackOpen] = useState<boolean>(false);
    const [isSaveDraftOpen, setIsSaveDraftOpen] = useState<boolean>(false);
    const [isPublishOpen, setIsPublishOpen] = useState<boolean>(false);
    const [isUnsavedPublishOpen, setIsUnsavedPublishOpen] =
        useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);

    useEffect(() => {
        if (!templateProfile && templateId) {
            getTemplateProfile(Number(templateId) as TemplateType)
                .then((profile) => setTemplateProfile(profile))
                .catch(setError);
        }
    }, [getTemplateProfile, templateId, templateProfile]);

    useEffect(() => {
        if (!replacementFields) {
            getReplacementFields().then(setReplacementFields).catch(setError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replacementFields]);

    function handleBack(): void {
        navigate(`/templates?type=${templateProfile?.type}`);
    }

    function handleBackOrPromt(): void {
        if (isDirty) {
            setIsConfirmBackOpen(true);
            return;
        }

        handleBack();
    }

    function handlePublishDraft(): void {
        if (!templateProfile) {
            return;
        }
        publishDraft(templateProfile.type).then((r) => {
            notifySuccess("Skabelonen er publiceret");
            handleBack();
        });
    }

    function handleUpdateDraft(): void {
        if (!templateProfile) {
            return;
        }
        updateDraft(templateProfile.type, templateProfile.draft!.content).then(
            (r) => {
                notifySuccess("Kladden er gemt");
                setIsDirty(false);
            }
        );
    }

    function handlePublishOrPromt() {
        if (isDirty) {
            setIsUnsavedPublishOpen(true);
            return;
        }

        setIsPublishOpen(true);
    }

    const isLoading = !templateProfile && !replacementFields;
    const draft = templateProfile?.draft;

    return isLoading && !error ? (
        <LoadingBoxFillComponent />
    ) : error ? (
        <ErrorPageScreen error={error} />
    ) : (
        <>
            <HeaderWithBack onBack={handleBackOrPromt}>
                <h1
                    className={
                        "text-xm align-middle text-secondary-gray-dark font-semibold"
                    }
                >
                    Rediger skabelon
                </h1>
            </HeaderWithBack>
            <div className={"flex justify-center"}>
                <>
                    {!!draft && (
                        <ContentBox className={"flex w-fit"}>
                            <div className={"space-y-32 "}>
                                <div
                                    className={
                                        "flex min-w-[800px] justify-between"
                                    }
                                >
                                    <ContentHeader className={"text-xm"}>
                                        {draft?.title ?? "[Title]"}
                                    </ContentHeader>
                                    <div className={"flex gap-20"}>
                                        <Button
                                            theme={"secondary"}
                                            onClick={() =>
                                                setIsSaveDraftOpen(true)
                                            }
                                        >
                                            Gem som kladde
                                        </Button>
                                        <Button
                                            disabled={isDirty}
                                            theme={"primary"}
                                            iconRight={true}
                                            onClick={() =>
                                                handlePublishOrPromt()
                                            }
                                        >
                                            Publicer
                                        </Button>
                                    </div>
                                </div>
                                <TinyMceEditor
                                    replacementFields={replacementFields!}
                                    value={draft!.content}
                                    setValue={(v) => {
                                        setIsDirty(true);
                                        draft!.content = v;
                                    }}
                                />
                            </div>
                        </ContentBox>
                    )}
                </>
            </div>
            <BasicDialog
                description={
                    "Du har ikke gemt, ugemte ændringer vil blive kasseret. Er du sikker på, at du vil afslutte?"
                }
                title={"Ugemte ændringer"}
                buttonA={{
                    label: "Ja",
                    onClick: handleBack
                }}
                isOpen={isConfirmBackOpen}
                setIsOpen={setIsConfirmBackOpen}
            />
            <BasicDialog
                description={
                    "Du har ikke gemt. Ikke gemte ændringer vil ikke blive kasseret. Er du sikker på, at du vil fortsætte?"
                }
                title={"Ikke gemte ændringer"}
                buttonA={{
                    label: "Ja",
                    onClick: () => setIsPublishOpen(true)
                }}
                isOpen={isUnsavedPublishOpen}
                setIsOpen={setIsUnsavedPublishOpen}
            />
            <BasicDialog
                description={
                    "Er du sikker på, at du vil publicere denne kladde?"
                }
                title={"Publicer kladde"}
                buttonA={{
                    label: "Ja",
                    onClick: handlePublishDraft
                }}
                isOpen={isPublishOpen}
                setIsOpen={setIsPublishOpen}
            />
            <BasicDialog
                description={"Er du sikker på, at gemme denne kladde?"}
                title={"Gem ændringer"}
                buttonA={{
                    label: "Ja",
                    onClick: handleUpdateDraft
                }}
                isOpen={isSaveDraftOpen}
                setIsOpen={setIsSaveDraftOpen}
            />
        </>
    );
}
